import { yupResolver } from "@hookform/resolvers/yup";
import dayjs from "dayjs";
import React, { FC, useEffect, useRef, useState } from "react";
import { FormProvider, useForm, useWatch } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useGetLoggedInUserDetails } from "../../../../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import { IdNameList } from "../../../../../../../../api/services/common/models";
import {
  useAddNISParticipantActivity,
  useGetActivityTypesListForNIS,
  useGetModalityByIdForNIS,
  useGetTopicListForNIS,
  useUpdateNISParticipantActivity,
} from "../../../../../../../../api/services/nisParticipants/nisParticipantsApis";
import { ParticipantActivityParams } from "../../../../../../../../api/services/program/models";
import {
  useGetActivityTypesListByProgramId,
  useGetModalityById,
  useGetTopicListByProgramId,
  useSaveParticipantActivityForProgramMutation,
  useUpdateParticipantActivityForProgramMutation,
} from "../../../../../../../../api/services/program/programApis";
import { useGetUsersDetailByRoleMutation } from "../../../../../../../../api/services/user/userApi";
import {
  AutoCompleteOption,
  FormAutoCompleteController,
  MuiAutoComplete,
  MuiBox,
  MuiGrid,
  MuiModal,
  OptionsDataType,
} from "../../../../../../../../components";
import { ActivityForm } from "../../../../../../../../components/activityForm/ActivityForm";
import { gridSize, ROLE_IDS } from "../../../../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../../../../helpers/constants/countryDateFormat";
import {
  convertToUTCFormat,
  getValueOrBlank,
  isNotEmpty,
  setApiDataToDropDownOption,
} from "../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { useToast } from "../../../../../../../../hooks/useToast";
import { toggleLoader } from "../../../../../../../../redux/slices/appState/appStateSlice";
import { CancelFieldModal } from "../../../../../../../createProgramTemplate/components/cancelFieldModal/CancelFieldModal";
import {
  AddActivityDefaultFormValue,
  AddActivityFormKey,
  AddActivityFormProps,
  ParticipantActivityResponse,
} from "../../../../../../modelsAndSchemas/Models";
import { AddActivitySchema } from "../../../../../../modelsAndSchemas/ValidationSchema";
import { activityTimelineSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { AddActivityWrapper } from "../../../../CreateProgramTabs.style";

interface ParticipantAddActivityFormProps {
  show: boolean;
  handleCloseModal: () => void;
  pageId: string;
  programId?: string;
  getListOfActivitiesApi: () => void;
  isEditActivity?: boolean;
  editActivityData?: ParticipantActivityResponse;
  customHeaderLabel?: string;
  isFromCoachDashboard?: boolean;
  participantListOption?: AutoCompleteOption[];
  inputValue?: string;
  setInputValue?: (val: string) => void;
  isFromNISParticipant?: boolean;
  nisParticipantStatus?: string;
}

export const ParticipantAddActivity: FC<ParticipantAddActivityFormProps> = ({
  show,
  handleCloseModal,
  pageId,
  programId,
  getListOfActivitiesApi,
  isEditActivity = false,
  editActivityData,
  customHeaderLabel,
  isFromCoachDashboard = false,
  participantListOption = [],
  inputValue,
  setInputValue,
  isFromNISParticipant,
  nisParticipantStatus,
}) => {
  const {
    fetchLabel,
    commonLabels,
    fetchDropdownValues,
    commonValidationMessages,
  } = useSiteCore();

  const [currentProgramId, setCurrentProgramId] = useState(programId);

  const dispatch = useDispatch();
  const { displayToast } = useToast();

  const [filteredModality, setFilteredModality] = useState<OptionsDataType[]>(
    [],
  );

  const topicRef = useRef<{ resetValues: (values?: string[]) => void }>(null);

  const [showCancel, setShowCancel] = useState<boolean>(false);
  const [isEffectExecuted, setIsEffectExecuted] = useState<boolean>(false);

  const getLabel = (key: string) => {
    return fetchLabel(activityTimelineSiteCoreKeys.activityTimeline, key);
  };

  const getDropdownValues = (key: string) => {
    return fetchDropdownValues(
      activityTimelineSiteCoreKeys.activityTimeline,
      key,
    );
  };
  const { loggedInUser } = useGetLoggedInUserDetails();

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const methods = useForm<AddActivityFormProps>({
    mode: "all",
    defaultValues: AddActivityDefaultFormValue,
    resolver: yupResolver(
      AddActivitySchema(
        {
          ...commonValidationMessages,
          participantSelectionRequiredErrorMessage: getLabel(
            activityTimelineSiteCoreKeys.participantSelectionIsRequiredErrorMessage,
          ),
        },
        isFromCoachDashboard,
      ),
    ),
  });

  const {
    control,
    getValues,
    setValue,
    reset,
    formState: { isDirty, dirtyFields },
    handleSubmit,
  } = methods;

  const addActivityWatchedValues = useWatch({
    name: AddActivityFormKey.chooseParticipant,
    control,
  });

  const isProgramIdValid = currentProgramId && isNotEmpty(currentProgramId);
  const { commonARIALabel } = useNavigationSiteCore();

  const {
    data: activityTypeListData,
    isFetching: isActivityTypePending,
    isError: isActivityTypeError,
    refetchWithParams: getActivityList,
  } = useGetActivityTypesListByProgramId();

  const {
    data: nisActivityTypeListData,
    isFetching: isNISActivityTypePending,
    isError: isNISActivityTypeError,
    refetchWithParams: getActivityListForNIS,
  } = useGetActivityTypesListForNIS();

  const {
    data: modalityListData,
    isFetching: isModalityPending,
    isError: isModalityError,
    isSuccess: isModalitySuccess,
    refetchWithParams: getModalityList,
    clearListOfModalityData,
  } = useGetModalityById();

  const {
    data: nisModalityListData,
    isFetching: isNISModalityPending,
    isError: isNISModalityError,
    isSuccess: isNISModalitySuccess,
    refetchWithParams: getNISModalityList,
    clearListOfModalityData: clearNISModalityData,
  } = useGetModalityByIdForNIS();

  const {
    data: topicListData,
    isFetching: isTopicPending,
    isError: isTopicError,
    isSuccess: isTopicSuccess,
    refetchWithParams: getTopicList,
    clearListOfTopicData,
  } = useGetTopicListByProgramId();

  const {
    data: nisTopicListData,
    isFetching: isNISTopicPending,
    isError: isNISTopicError,
    isSuccess: isNISTopicSuccess,
    refetchWithParams: getNISTopicList,
    clearListOfTopicData: clearNISTopicData,
  } = useGetTopicListForNIS();

  const {
    mutateAsync: getProgramCoaches,
    data: programCoachList,
    isPending: isCoachPending,
    isError: isCoachError,
  } = useGetUsersDetailByRoleMutation();

  const {
    mutateAsync: saveActivityApi,
    isPending: isSaveActivityPending,
    isError: isSaveActivityError,
    isSuccess: isSaveActivitySuccess,
  } = useSaveParticipantActivityForProgramMutation();

  const {
    mutateAsync: addNISActivityApi,
    isPending: isAddNISActivityPending,
    isError: isAddNISActivityError,
    isSuccess: isAddNISActivitySuccess,
  } = useAddNISParticipantActivity();

  const {
    mutateAsync: updateNISActivityApi,
    isPending: isUpdateNISActivityPending,
    isError: isUpdateNISActivityError,
    isSuccess: isUpdateNISActivitySuccess,
  } = useUpdateNISParticipantActivity();

  const {
    mutateAsync: updateActivityApi,
    isPending: isUpdateActivityPending,
    isError: isUpdateActivityError,
    isSuccess: isUpdateActivitySuccess,
  } = useUpdateParticipantActivityForProgramMutation();

  useEffect(() => {
    if (isFromNISParticipant) {
      getActivityListForNIS(getValueOrBlank(nisParticipantStatus));
    } else if (currentProgramId) {
      getActivityList(currentProgramId);
      getProgramCoaches({ roleIds: [ROLE_IDS.deliveryCoach] });
    }
  }, [currentProgramId, isFromNISParticipant, nisParticipantStatus]);

  useEffect(() => {
    if (isEditActivity && isNotEmpty(editActivityData) && !isEffectExecuted) {
      getModalityApi(getValueOrBlank(editActivityData?.activityType));
      getTopicApi(
        getValueOrBlank(editActivityData?.activityType),
        getValueOrBlank(editActivityData?.modality),
      );

      const topicIds = editActivityData?.topics?.map((topic) => topic.id) ?? [];

      reset({
        [AddActivityFormKey.activityType]: getValueOrBlank(
          editActivityData?.activityType,
        ),
        [AddActivityFormKey.modality]: getValueOrBlank(
          editActivityData?.modality,
        ),
        [AddActivityFormKey.topic]: topicIds,
        [AddActivityFormKey.notes]: getValueOrBlank(editActivityData?.notes),
        [AddActivityFormKey.actualDurationInMinutes]: getValueOrBlank(
          editActivityData?.actualDurationInMinutes,
        ),
        [AddActivityFormKey.onBehalfOfCoachId]: getValueOrBlank(
          editActivityData?.onBehalfOfCoach?.id,
        ),
        [AddActivityFormKey.activityDate]:
          dayjs(editActivityData?.activityDate).format(userCountryDateFormat) ??
          "",
      });

      const currentTopicListData = isFromNISParticipant
        ? nisTopicListData
        : topicListData;
      if (isNotEmpty(currentTopicListData)) {
        setIsEffectExecuted(true);
        topicRef.current?.resetValues(topicIds);
      }
    }
    if (!isEditActivity) {
      // current date as default for activity date
      setValue(
        AddActivityFormKey.activityDate,
        dayjs().format(userCountryDateFormat),
      );
    }
  }, [
    isEditActivity,
    editActivityData,
    isTopicSuccess,
    topicListData,
    isNISTopicSuccess,
    nisTopicListData,
    isFromNISParticipant,
  ]);

  useEffect(() => {
    if (
      isSaveActivitySuccess ||
      isUpdateActivitySuccess ||
      isAddNISActivitySuccess ||
      isUpdateNISActivitySuccess
    ) {
      const toastMessage =
        (isUpdateActivitySuccess || isUpdateNISActivitySuccess) &&
        isEditActivity
          ? getLabel(activityTimelineSiteCoreKeys.activityUpdatedSuccessText)
          : getLabel(activityTimelineSiteCoreKeys.activityAddedSuccessText);
      displayToast(toastMessage);
      handleCloseModal();
      getListOfActivitiesApi();
    }
  }, [
    isSaveActivitySuccess,
    isUpdateActivitySuccess,
    isAddNISActivitySuccess,
    isUpdateNISActivitySuccess,
  ]);

  useEffect(() => {
    dispatch(
      toggleLoader({
        isVisible:
          isActivityTypePending ||
          isNISActivityTypePending ||
          isModalityPending ||
          isNISModalityPending ||
          isTopicPending ||
          isNISTopicPending ||
          isSaveActivityPending ||
          isAddNISActivityPending ||
          isCoachPending ||
          isUpdateActivityPending ||
          isUpdateNISActivityPending,
        message: commonLabels.loadingPleaseWait,
      }),
    );
    return () => {
      dispatch(toggleLoader({ isVisible: false }));
    };
  }, [
    isActivityTypePending,
    isNISActivityTypePending,
    isModalityPending,
    isNISModalityPending,
    isTopicPending,
    isNISTopicPending,
    isCoachPending,
    isSaveActivityPending,
    isAddNISActivityPending,
    isUpdateNISActivityPending,
    isUpdateActivityPending,
  ]);

  useEffect(() => {
    if (
      isActivityTypeError ||
      isNISActivityTypeError ||
      isModalityError ||
      isNISModalityError ||
      isTopicError ||
      isNISTopicError ||
      isSaveActivityError ||
      isAddNISActivityError ||
      isUpdateActivityError ||
      isUpdateNISActivityError ||
      isCoachError
    ) {
      displayToast(commonLabels.someThingWentWrongPleaseTryAgainLater, "error");
      dispatch(toggleLoader({ isVisible: false }));
    }
  }, [
    isActivityTypeError,
    isNISActivityTypeError,
    isModalityError,
    isNISModalityError,
    isTopicError,
    isNISTopicError,
    isSaveActivityError,
    isAddNISActivityError,
    isUpdateActivityError,
    isUpdateNISActivityError,
    isCoachError,
  ]);

  useEffect(() => {
    const currentModalityListData = isFromNISParticipant
      ? nisModalityListData
      : modalityListData;
    const currentModalitySuccess = isFromNISParticipant
      ? isNISModalitySuccess
      : isModalitySuccess;

    if (currentModalitySuccess && currentModalityListData) {
      // set modality list data to dropdown
      const dropdownValues = getDropdownValues(
        activityTimelineSiteCoreKeys.modalityList,
      );

      const filtered = dropdownValues?.filter((item) =>
        currentModalityListData.includes(item.value),
      );

      setFilteredModality(filtered);
    }
  }, [
    isModalitySuccess,
    modalityListData,
    isNISModalitySuccess,
    nisModalityListData,
    isFromNISParticipant,
  ]);

  const closeMainModalHandler = () => {
    if (isDirty) setShowCancel(true);
    else handleCloseModal();
  };

  const activityTitle = React.useMemo(() => {
    return () => {
      if (customHeaderLabel) return customHeaderLabel;
      return isEditActivity
        ? getLabel(activityTimelineSiteCoreKeys.editActivityHeader)
        : getLabel(activityTimelineSiteCoreKeys.addActivityHeader);
    };
  }, [customHeaderLabel, isEditActivity]);

  const getFormattedActivityDate = (activityDate?: string): string => {
    return activityDate
      ? convertToUTCFormat(activityDate, userCountryDateFormat)
      : "";
  };

  const buildBaseParams = (): ParticipantActivityParams => {
    const {
      activityType,
      modality,
      topic,
      notes,
      onBehalfOfCoachId,
      actualDurationInMinutes,
      activityDate,
    } = getValues();
    const activityTypeData = isFromNISParticipant
      ? nisActivityTypeListData
      : activityTypeListData;

    return {
      activityType,
      activityName:
        activityTypeData?.find((item: IdNameList) => item.id === activityType)
          ?.name ?? "",
      modality,
      topic,
      notes,
      onBehalfOfCoachId: isFromNISParticipant ? undefined : onBehalfOfCoachId,
      actualDurationInMinutes,
      activityDate: getFormattedActivityDate(activityDate),
    };
  };

  const handleUpdateActivity = () => {
    if (!editActivityData?.id) return;
    const baseParams = buildBaseParams();
    const updatedParams = {
      params: {
        ...baseParams,
        programId,
        ...(isFromNISParticipant && { nisParticipantId: programId }),
        ...(!dirtyFields?.activityDate && {
          activityDate: editActivityData.activityDate,
        }),
      },
      participantActivityTimeLineId: editActivityData.id,
    };

    isFromNISParticipant
      ? updateNISActivityApi(updatedParams)
      : updateActivityApi(updatedParams);
  };

  const handleAddActivity = () => {
    if (!isProgramIdValid) return;
    const baseParams = buildBaseParams();
    const addActivityParams = {
      params: baseParams,
      programId: currentProgramId,
    };

    isFromNISParticipant
      ? addNISActivityApi(addActivityParams)
      : saveActivityApi(addActivityParams);
  };

  const onSubmitClick = () => {
    if (isEditActivity) {
      handleUpdateActivity();
    } else {
      handleAddActivity();
    }
  };

  const onConfirmClick = () => {
    handleSubmit(onSubmitClick)();
  };

  const closeModalHandler = () => setShowCancel(false);

  const cancelConfirmHandler = () => {
    setShowCancel(false);
    displayToast(
      isEditActivity
        ? getLabel(activityTimelineSiteCoreKeys.cancelConfirmationText)
        : getLabel(activityTimelineSiteCoreKeys.changesDiscardedText),
    );
    handleCloseModal();
  };

  const handleActivityTypeChange = (activityTypeKey: string) => {
    setIsEffectExecuted(true);
    resetModality();
    resetTopic();
    getModalityApi(activityTypeKey);
  };

  const getModalityApi = (activityTypeKey: string) => {
    if (isFromNISParticipant) {
      if (activityTypeKey) {
        setTimeout(() => {
          getNISModalityList(activityTypeKey);
        }, 0);
      }
    } else if (activityTypeKey && isProgramIdValid) {
      setTimeout(() => {
        getModalityList(currentProgramId, activityTypeKey);
      }, 0);
    }
  };

  const resetModality = () => {
    if (isFromNISParticipant) {
      clearNISModalityData();
    } else {
      clearListOfModalityData();
    }
    setFilteredModality([]);
    setValue(AddActivityFormKey.modality, "");
  };

  const resetTopic = () => {
    if (isFromNISParticipant) {
      clearNISTopicData();
    } else {
      clearListOfTopicData();
    }
    setValue(AddActivityFormKey.topic, []);
    topicRef.current?.resetValues([]);
  };

  const handleModalityChange = (modalityKey: string) => {
    setIsEffectExecuted(true);
    resetTopic();
    const activityTypeKey = getValues(AddActivityFormKey.activityType);
    if (activityTypeKey) {
      getTopicApi(activityTypeKey, modalityKey);
    }
  };

  const getTopicApi = (activityTypeKey: string, modalityKey: string) => {
    if (isFromNISParticipant) {
      getNISTopicList(activityTypeKey, getValueOrBlank(nisParticipantStatus));
    } else if (isProgramIdValid) {
      getTopicList(currentProgramId, activityTypeKey, modalityKey);
    }
  };

  useEffect(() => {
    // set selected value from autoComplete dropdown to current program id
    if (isFromCoachDashboard) {
      setCurrentProgramId(addActivityWatchedValues);
    }
  }, [addActivityWatchedValues, isFromCoachDashboard]);

  const renderActivityForm = () => {
    return (
      <MuiBox
        className={`activity-form-container ${isFromCoachDashboard ? "padding-top" : ""}`}
      >
        <ActivityForm
          pageId={pageId}
          filteredActivityTypes={setApiDataToDropDownOption(
            isFromNISParticipant
              ? nisActivityTypeListData
              : activityTypeListData,
          )}
          filteredModality={filteredModality}
          topicListData={
            isFromNISParticipant ? nisTopicListData : topicListData
          }
          programCoachList={programCoachList ?? []}
          topicRef={topicRef}
          handleActivityTypeChange={handleActivityTypeChange}
          handleModalityChange={handleModalityChange}
          isBulkAdd={false}
          isFromNISParticipant={isFromNISParticipant}
        />
      </MuiBox>
    );
  };

  const renderCoachDashboardLayout = () => {
    return (
      <FormProvider {...methods}>
        <MuiGrid container>
          <MuiGrid
            size={gridSize}
            className={`participant-container ${isProgramIdValid && "padding-bottom"}`}
          >
            <FormAutoCompleteController
              control={control}
              name={AddActivityFormKey.chooseParticipant}
            >
              <MuiAutoComplete
                label={getLabel(
                  activityTimelineSiteCoreKeys.chooseParticipantDropDownLabel,
                )}
                placeholder={commonLabels.typeToSearchPlaceholder3Chars}
                requiredARIALabel={commonARIALabel.required}
                options={participantListOption}
                inputValue={inputValue}
                setInputValue={setInputValue}
                isInputValueControlled
                id={`${pageId}-${AddActivityFormKey.chooseParticipant}`}
                noOptionsText={commonLabels.noResult}
                isMandatory
              />
            </FormAutoCompleteController>
          </MuiGrid>
        </MuiGrid>

        {isProgramIdValid && (
          <>
            <MuiBox className="dash-separator" />
            {renderActivityForm()}
          </>
        )}
      </FormProvider>
    );
  };

  const renderAddActivityFormLayout = () => {
    return <FormProvider {...methods}>{renderActivityForm()}</FormProvider>;
  };

  return (
    <MuiModal
      open={show}
      onClose={closeMainModalHandler}
      title={activityTitle()}
      primaryButtonTitle={
        isEditActivity ? commonLabels.update : commonLabels.save
      }
      secondaryButtonTitle={commonLabels.close}
      onPrimaryButtonClick={onConfirmClick}
      onSecondaryButtonClick={closeMainModalHandler}
      hasLargeContent
    >
      <AddActivityWrapper data-testid={`${pageId}-add-participant-activity`}>
        {isFromCoachDashboard
          ? renderCoachDashboardLayout()
          : renderAddActivityFormLayout()}

        {showCancel && (
          <CancelFieldModal
            showModal={showCancel}
            handleModalClose={closeModalHandler}
            handlePrimaryButtonClick={cancelConfirmHandler}
            cancelMessage={getLabel(
              activityTimelineSiteCoreKeys.closeConfirmationText,
            )}
          />
        )}
      </AddActivityWrapper>
    </MuiModal>
  );
};
